<script>
/**
 * @component Indication
 * @description Sección acordeón de indicación médica con toggle,
 *              formulario dinámico y soporte para múltiples indicaciones.
 * @example <Indications :form="form" :savedIndications="[]" />
 */
import Vue from "vue";
import { mapGetters } from "vuex";
import { theme } from "@/services/config";
import leave from "@/mixins/leave";
import { v4 as uuidv4 } from "uuid";
import fileUploadConfigService from "@/services/fileupload-config";

import Spinner from "@/components/ui/Spinner";
import FileUpload from "@/components/ui/FileUpload";
import InputTag from "@/components/ui/InputTag";
import { cloneDeep } from "lodash";
import ImagesSelect from "@/components/ui/ImagesSelect.vue";
import LabSelect from "@/components/ui/LabSelect.vue";
import DiagnosisSelect from "@/components/ui/DiagnosisSelect.vue";
import MedicationSelect from "@/components/ui/MedicationSelect.vue";
import ConsultationSelect from "@/components/ui/ConsultationSelect.vue";
import MultiDatePicker from "@/components/ui/MultiDatePicker.vue";
import FormSelect from "@/components/ui/FormSelect.vue";
import FormRenderer from "@/components/ui/FormRenderer.vue";
import ProgramSelect from "@/components/ui/ProgramSelect.vue";
import { getIndicationForm, INDICATIONS_TYPES } from "@/services/constants";
import indicationService from "@/services/indication";
import { isValidMongoId } from "@/utils/validators";

/** @description Mapa de iconos por tipo de sección */
const SECTION_ICONS = {
  program: "el-icon-files",
  evolution: "el-icon-document",
  medications: "el-icon-first-aid-kit",
  images: "el-icon-camera",
  lab: "el-icon-receiving",
  practices: "el-icon-document-checked",
  notes: "el-icon-edit",
  certificates: "el-icon-trophy",
  forms: "el-icon-document-copy",
};

export default {
  name: "Indication",
  mixins: [leave],

  components: {
    Spinner,
    FileUpload,
    InputTag,
    DiagnosisSelect,
    MedicationSelect,
    ImagesSelect,
    LabSelect,
    ConsultationSelect,
    MultiDatePicker,
    FormSelect,
    FormRenderer,
    ProgramSelect,
  },

  props: {
    form: {
      type: Object,
      required: true,
    },
    savedIndications: {
      type: Array,
      required: true,
    },
    appointment: {
      type: Object,
      default: null,
    },
    existingIndication: {
      type: Object,
      default: null,
    },
    mode: {
      type: String,
      default: "create",
      validator: (value) => ["create", "edit"].includes(value),
    },
    typeName: {
      type: String,
      default: null,
    },
    patient: {
      type: Object,
    },
    /**
     * @description Oculta el header del accordion cuando se usa dentro de AppointmentFormV2.
     */
    hideHeader: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      theme,
      isLoading: false,
      isPosting: false,
      hasFiles: false,
      isFetchingIndication: false,
      usesExternalMedication: true,
      mustShowForm: false,
      isSaveDisabled: true,
      isAddNewDisabled: false,
      isSelected: this.form?.type === "evolution",
      indications: [],
      isFormModalVisible: false,
      selectedForm: null,
      prefillData: null,
      isEditing: false,
      completedForms: {},
      selectedFormForRenderer: null,
      isFormRendererVisible: false,
      editingFormData: null,
      isEditingForm: false,
      selectedProgram: null,
      programForms: [],
      completedProgramForms: {},
      activeCollapse: [],
      prescriptionForm: getIndicationForm("medications"),
      prescriptionTouched: false,
      prescriptionCompleted: false,
      prescriptionDisabled: false,
    };
  },

  async created() {
    if (this.mode !== "edit") {
      this.addIndication();
    }
    await this.getForms();
  },

  computed: {
    ...mapGetters(["isAdmin", "isDoctor", "getDiagnosisById"]),

    currentPatient() {
      const isAppointmentRoute =
        this.$route.name === "appointment" ||
        this.$route.path.includes("/appointment/");

      let patientId;

      if (isAppointmentRoute) {
        if (this.appointment && this.appointment.patient) {
          patientId = this.appointment.patient || this.appointment.patient._id;
        } else {
          console.warn("No patient found in appointment data");
          return null;
        }
      } else {
        patientId = this.$route.params.id;
        if (!patientId) {
          console.warn("No patient ID found in route params");
          return null;
        }
      }

      return {
        _id: patientId,
      };
    },
    actualIndications() {
      if (this.mode === "edit" && this.existingIndication) {
        return [this.prepareIndicationForEdit(this.existingIndication)];
      }
      return this.indications;
    },

    /** @returns {string} Icono de Element UI para el tipo de sección */
    sectionIcon() {
      return SECTION_ICONS[this.form.type] || "el-icon-document";
    },
    /**
     * @description Indicaciones guardadas para este tipo específico.
     * Usado para mostrar el badge de cantidad en el header.
     * @returns {Array}
     */
    savedIndicationsForType() {
      return (this.savedIndications || []).filter(
        (ind) => ind.type === this.form.type && ind.createdAt
      );
    },
  },

  watch: {
    indications: {
      handler(newVal) {
        if (newVal.length < 1 && this.mode !== "edit") {
          this.addIndication();
        }
      },
    },
  },

  methods: {
    getSignatureAsBase64() {
      const doctor = this.$store.state.user;
      return doctor?.doctor?.signature || null;
    },

    async savePrescriptionIndication() {
      try {
        this.isPosting = true;
        this.showLeaveGuard = false;

        const formData = new FormData();

        const prescription = {
          type: "medications",
          patientId: this.currentPatient._id,
          doctorId: this.$store.state.user?._id || null,
          programName: this.selectedProgram?.name || "",
          diagnosis: null,
          publicMedication: null,
        };

        const signatureBase64 = this.getSignatureAsBase64();
        if (signatureBase64) {
          prescription.doctorSignature = signatureBase64;
        }

        const fieldMap = {
          cantidad: "quantity",
          posologia: "posology",
          observaciones: "observations",
          "tratamiento prolongado": "isLongTerm",
          diagnostico: "diagnosis",
        };

        this.prescriptionForm.forEach((field) => {
          const val = field.value;
          if (val === undefined || val === null || val === "") return;

          const rawKey = field.name || field.friendlyName || "";
          const key = rawKey.toLowerCase().trim();
          const backendKey = fieldMap[key] || key;

          if (field.inputType === "diagnosisSelect") {
            if (typeof val === "object") {
              prescription.diagnosis = {
                id: val._id || val.id,
                name: val.description || val.name || "",
              };
            } else {
              prescription.diagnosis = { id: val, name: "" };
            }

            if (formData.has("diagnosis")) formData.delete("diagnosis");
            formData.append(
              "diagnosis",
              JSON.stringify(prescription.diagnosis)
            );
            return;
          }

          if (field.inputType === "medicationSelect") {
            const medication = this.$store.state.cacheMedications?.[val];
            if (medication) {
              prescription.publicMedication = {
                drugId: medication._id,
                name: medication.name,
                brandName: medication.brandName,
                drug: medication.drug,
                presentation: medication.presentation,
              };
              if (formData.has("publicMedication"))
                formData.delete("publicMedication");
              formData.append(
                "publicMedication",
                JSON.stringify(prescription.publicMedication)
              );
            }
            return;
          }

          let value = val;
          if (backendKey === "quantity") {
            value = Number(Array.isArray(val) ? val[0] : val);
          } else if (backendKey === "isLongTerm") {
            value = val === "Sí" || val === true;
          } else if (typeof val === "object") {
            value = JSON.stringify(val);
          }

          if (formData.has(backendKey)) {
            formData.delete(backendKey);
          }
          formData.append(backendKey, value);
        });

        if (formData.has("type")) formData.delete("type");
        if (formData.has("patientId")) formData.delete("patientId");
        formData.append("type", prescription.type);
        formData.append("patientId", prescription.patientId);

        if (prescription.doctorId) {
          if (formData.has("doctorId")) formData.delete("doctorId");
          formData.append("doctorId", prescription.doctorId);
        }

        if (prescription.doctorSignature) {
          if (formData.has("doctorSignature"))
            formData.delete("doctorSignature");
          formData.append("doctorSignature", prescription.doctorSignature);
        }

        const indicationService = (await import("@/services/indication"))
          .default;
        const segmentService = (await import("@/services/segment")).default;

        segmentService.track({ name: "Indication Created" });

        const created = await indicationService.createIndication(formData);
        this.prescriptionCompleted = true;
        this.prescriptionDisabled = true;

        if (this.hasFiles && this.$refs.fileUpload) {
          this.$refs.fileUpload.startUpload();
        }

        this.$message.success("Prescripción generada correctamente");
        this.activeCollapse = "";
        this.$bus.$emit("indication-created", created);
      } catch (error) {
        console.error("Error al guardar la prescripción:", error);
        const errorMessage =
          error.response?.data?.error ||
          "Error al guardar la prescripción";
        this.$message.error(errorMessage);
      } finally {
        this.isPosting = false;
      }
    },
    uploadMediaEndpoint(indicationId) {
      return fileUploadConfigService.getIndicationFilesEndpoint(indicationId);
    },

    async triggerUpload(createdIndication) {
      const index = this.indications.findIndex(
        (indication) => indication.key === createdIndication.key
      );

      this.$set(this.indications, index, {
        ...this.indications[index],
        _id: createdIndication._id,
      });

      await Vue.nextTick();

      const refName = `fileUpload-${createdIndication.key}`;
      this.$refs[refName][0].startUpload();
    },

    updateIndication(indication, isDeleted = false) {
      this.$bus.$emit("updateIndication", cloneDeep(indication), isDeleted);
    },

    saveOrEditText(indication) {
      return indication.lockedAt ? "Editar" : "Guardar";
    },

    submit(indication) {
      if (this.mode === "edit") {
        this.submitEdit(indication);
      } else {
        if (indication.lockedAt) {
          indication.lockedAt = null;
        } else {
          indication.lockedAt = new Date();

          if (indication.type === "program" && this.selectedProgram) {
            indication.programData = {
              name: this.selectedProgram.name,
              description: this.selectedProgram.description,
              status: "pending",
            };

            const completedForms = this.getCompletedProgramFormsForIndication(
              indication.key,
              this.selectedProgram._id
            );
            if (completedForms && completedForms.length > 0) {
              indication.completedProgramForms = completedForms;
            }
          }
        }

        this.updateIndication(indication);
      }
    },

    prepareIndicationForEdit(existingIndication) {
      const fields = getIndicationForm(existingIndication.type);
      const defaultValues = {};
      const originalDiagnosis = {};

      for (const field of fields) {
        if (field.inputType === "title") {
          continue;
        }

        const fieldLabel = field.label || field.name;
        const existingValue = existingIndication[fieldLabel];

        if (field.inputType === "diagnosisSelect") {
          if (!existingValue) {
            defaultValues[fieldLabel] = "";
          } else if (typeof existingValue === "string") {
            defaultValues[fieldLabel] = existingValue;
          } else {
            defaultValues[fieldLabel] =
              existingValue.id || existingValue._id || "";
            if (existingValue.id || existingValue._id) {
              originalDiagnosis[existingValue.id || existingValue._id] =
                existingValue;
            }
          }
        } else {
          defaultValues[fieldLabel] =
            existingValue !== undefined ? existingValue : field.value;
        }
      }

      return {
        key: `edit-${existingIndication._id}`,
        _id: existingIndication._id,
        type: existingIndication.type,
        defaultValues: fields.map((f) => ({
          ...f,
          value:
            defaultValues[f.label || f.name] !== undefined
              ? defaultValues[f.label || f.name]
              : f.value,
        })),
        originalDiagnosis,
        createdAt: existingIndication.createdAt,
        lockedAt: null,
      };
    },

    async submitEdit(indication) {
      try {
        this.isPosting = true;

        const payload = this.buildPayloadFromIndication(indication);

        await this.$confirm(
          `¿Está seguro que desea guardar los cambios realizados en la ${
            this.typeName || "indicación"
          }?`,
          "Confirmar cambios",
          {
            confirmButtonText: "Guardar",
            cancelButtonText: "Cancelar",
            type: "question",
          }
        );

        const immutableFields = [
          "_id",
          "organization",
          "patient",
          "episode",
          "doctor",
          "type",
          "createdBy",
          "createdAt",
          "files",
          "pdfUrl",
          "archivedAt",
          "cancelledAt",
        ];

        const cleanPayload = {};
        Object.keys(payload).forEach((key) => {
          if (!immutableFields.includes(key)) {
            cleanPayload[key] = payload[key];
          }
        });

        await indicationService.update(indication._id, cleanPayload);

        const indicationTypeName =
          this.typeName ||
          INDICATIONS_TYPES.find((t) => t.type === indication.type)?.name ||
          "indicación";

        this.$message.success(
          `${indicationTypeName} actualizada correctamente`
        );

        this.$emit("saved", {
          ...cleanPayload,
          _id: indication._id,
          type: indication.type,
        });
      } catch (error) {
        if (error !== "cancel") {
          console.error("Error al guardar indicación:", error);
          const indicationTypeName =
            this.typeName ||
            INDICATIONS_TYPES.find((t) => t.type === indication.type)?.name ||
            "indicación";
          const errorMessage =
            error.response?.data?.error ||
            `Error al guardar la ${indicationTypeName}`;
          this.$message.error(errorMessage);
        }
      } finally {
        this.isPosting = false;
      }
    },

    buildPayloadFromIndication(indication) {
      const payload = { ...this.existingIndication };
      const fields = getIndicationForm(indication.type);

      for (const field of fields) {
        if (field.inputType === "title") {
          continue;
        }

        const fieldLabel = field.label || field.name;
        const fieldData = indication.defaultValues?.find(
          (f) => (f.label || f.name) === fieldLabel
        );
        let val = fieldData?.value;

        if (field.inputType === "diagnosisSelect") {
          if (!val) {
            val = null;
          } else if (isValidMongoId(val)) {
            const diagnosis = this.getDiagnosisById(val);
            const originalDiagnosis = indication.originalDiagnosis?.[val];
            val = {
              id: val,
              name: diagnosis?.description || originalDiagnosis?.name || "",
            };
          } else if (typeof val === "object" && val.id) {
            val = {
              id: val.id || val._id || null,
              name: val.name || val.description || "",
            };
          }
        }

        payload[fieldLabel] = val;
      }

      return payload;
    },

    cancelEdit() {
      if (this.mode === "edit") {
        this.$emit("cancel");
      }
    },

    canEdit(indication) {
      if (this.mode === "edit") {
        return true;
      }
      return !indication.lockedAt || indication.createdAt;
    },

    revertIndication(indication, index) {
      const previousIndication = this.savedIndications.find(
        (e) => e.key === indication.key
      );

      if (previousIndication) {
        this.$set(this.indications, index, {
          ...cloneDeep(previousIndication),
          lockedAt: new Date(),
        });
        this.updateIndication(this.indications[index]);
      } else {
        this.deleteIndication(indication.key);
      }

      if (this.indications.length < 1) {
        this.isSelected = false;
      }
    },

    deleteAllIndications() {
      this.indications
        .filter((indication) => !indication.createdAt)
        .forEach((indication) => {
          this.deleteIndication(indication.key);
        });
    },

    getFileName(key = "") {
      return key.split("/").pop();
    },

    getDropdownOptions() {
      return {
        method: "put",
        uploadMultiple: true,
        maxFiles: 8,
        maxFilesize: 5,
        acceptedFiles: "image/*,application/pdf",
      };
    },

    /**
     * @description Llama a triggerAISearch en el selector del tipo indicado dentro de una indicación.
     *              Usado por IndicationForm para pre-filtrar opciones de dropdown tras sugerencia de IA.
     * @param {string} indicationKey - key único de la indicación (uuid)
     * @param {string} inputType - inputType del selector ('diagnosisSelect', 'medicationSelect', 'labSelect', 'imagesSelect')
     * @param {string} term - término de búsqueda sugerido por la IA
     */
    triggerSelectorSearch(indicationKey, inputType, term) {
      const ref = this.$refs[`sel-${indicationKey}-${inputType}`];
      const component = Array.isArray(ref) ? ref[0] : ref;
      if (component && component.triggerAISearch) {
        component.triggerAISearch(term);
      }
    },

    /**
     * @description Devuelve la última indicación para aplicar sugerencias de IA.
     *              Para formularios sin allowMultiple (evolution, images, lab) siempre reutiliza
     *              la existente — nunca crea duplicados, aunque tenga datos pre-cargados.
     *              Para formularios con allowMultiple (medications) reutiliza si la última está vacía,
     *              o agrega una nueva si ya fue editada.
     * @returns {Object} La indicación a usar (reutilizada o nueva)
     */
    getOrAddIndication() {
      if (this.indications.length > 0) {
        // Formularios de entrada única: siempre reusar, nunca duplicar
        if (!this.form?.allowMultiple) {
          return this.indications[this.indications.length - 1];
        }
        // Formularios con allowMultiple: reusar sólo si la última está vacía
        const last = this.indications[this.indications.length - 1];
        const dv = last.defaultValues;
        const fields = dv ? (Array.isArray(dv) ? dv : Object.values(dv)) : [];
        const requiredFields = fields.filter(
          (f) => f.required && f.inputType !== "title"
        );
        const checkFields =
          requiredFields.length > 0
            ? requiredFields
            : fields.filter(
                (f) => f.inputType !== "title" && f.inputType !== "select"
              );
        const isEmpty = checkFields.every((f) => !f.value);
        if (isEmpty) return last;
      }
      this.addIndication();
      return this.indications[this.indications.length - 1];
    },

    addIndication() {
      const newIndications = {
        key: uuidv4(),
        ...cloneDeep(this.form),
      };
      if (
        newIndications.type == "medications" &&
        newIndications.defaultValues?.patientEmail
      ) {
        const patientEmail =
          this.appointment?.patient?.email || this.patient?.email || "";
        newIndications.defaultValues.patientEmail.value = patientEmail;
      }
      this.indications.push(newIndications);
    },

    deleteIndication(key) {
      const index = this.indications.findIndex((i) => i.key === key);
      this.updateIndication(this.indications[index], true);
      this.indications.splice(index, 1);
    },

    uploadFilesSuccess(indicationKey) {
      this.$bus.$emit("filesUploaded", indicationKey);
    },

    async onFilesUpdated(files, indication) {
      await Vue.nextTick();
      const index = this.indications.findIndex((i) => i.key === indication.key);

      if (index !== -1) {
        this.$set(this.indications, index, {
          ...indication,
          hasFiles: !!files?.length,
        });
      }
    },

    updateDiagnosis(diagnosis, data) {
      this.$set(data, "diagnosis", diagnosis);
    },

    onFormSelected(form) {
      this.selectedFormForRenderer = { ...form };
      this.isFormRendererVisible = true;
    },

    onFormRendererCompleted(completedForm, indicationKey) {
      this.onFormCompleted(completedForm, indicationKey);
      this.isFormRendererVisible = false;
      this.selectedFormForRenderer = null;
    },

    onFormRendererCancel() {
      this.isFormRendererVisible = false;
      this.selectedFormForRenderer = null;
      this.editingFormData = null;
      this.isEditingForm = false;
    },

    onProgramSelected(program) {
      this.selectedProgram = program;
      this.programForms = program.initialData || [];
    },

    onProgramFormCompleted(completedForm, indicationKey) {
      if (!this.completedProgramForms[indicationKey]) {
        this.$set(this.completedProgramForms, indicationKey, {});
      }

      if (
        !this.completedProgramForms[indicationKey][this.selectedProgram._id]
      ) {
        this.$set(
          this.completedProgramForms[indicationKey],
          this.selectedProgram._id,
          []
        );
      }

      const existingIndex = this.completedProgramForms[indicationKey][
        this.selectedProgram._id
      ].findIndex((f) => f._id === completedForm._id);

      if (existingIndex >= 0) {
        this.$set(
          this.completedProgramForms[indicationKey][this.selectedProgram._id],
          existingIndex,
          completedForm
        );
      } else {
        this.completedProgramForms[indicationKey][
          this.selectedProgram._id
        ].push(completedForm);
      }

      this.$bus.$emit("program-form-completed", completedForm);
    },

    getCompletedProgramFormsForIndication(indicationKey, programId) {
      return this.completedProgramForms[indicationKey]?.[programId] || [];
    },

    areAllProgramFormsCompleted(indicationKey, programId) {
      const completedForms = this.getCompletedProgramFormsForIndication(
        indicationKey,
        programId
      );
      return (
        completedForms.length === this.programForms.length &&
        this.programForms.length > 0
      );
    },

    isFormCompletedForProgram(formId, indicationKey) {
      if (!this.completedProgramForms[indicationKey] || !this.selectedProgram) {
        return false;
      }

      const completedForms =
        this.completedProgramForms[indicationKey][this.selectedProgram._id] ||
        [];
      return completedForms.some(
        (completedForm) => completedForm._id === formId
      );
    },

    onFormRendererUpdated(updatedForm) {
      const indicationKey = this.editingFormData?.indicationKey;

      if (indicationKey && this.completedForms[indicationKey]) {
        const index = this.completedForms[indicationKey].findIndex(
          (f) => f._id === updatedForm._id
        );

        if (index >= 0) {
          this.$set(this.completedForms[indicationKey], index, updatedForm);
        }
      }

      this.isFormRendererVisible = false;
      this.selectedFormForRenderer = null;
      this.editingFormData = null;
      this.isEditingForm = false;
    },

    onFormCompleted(completedForm, indicationKey) {
      if (!this.completedForms[indicationKey]) {
        this.$set(this.completedForms, indicationKey, []);
      }

      const existingIndex = this.completedForms[indicationKey].findIndex(
        (f) => f._id === completedForm._id
      );

      if (existingIndex >= 0) {
        this.$set(
          this.completedForms[indicationKey],
          existingIndex,
          completedForm
        );
      } else {
        this.completedForms[indicationKey].push(completedForm);
      }

      const indication = this.indications.find((i) => i.key === indicationKey);
      if (indication) {
        if (
          indication.defaultValues &&
          typeof indication.defaultValues === "object" &&
          !Array.isArray(indication.defaultValues)
        ) {
          if (indication.defaultValues.form?.inputType === "formSelect") {
            indication.defaultValues.form.value = "";
          }
        } else if (Array.isArray(indication.defaultValues)) {
          const formField = indication.defaultValues.find(
            (f) => f.inputType === "formSelect"
          );
          if (formField) {
            formField.value = "";
          }
        }
      }
    },

    onFormUpdated(updatedForm, indicationKey) {
      if (this.completedForms[indicationKey]) {
        const existingIndex = this.completedForms[indicationKey].findIndex(
          (f) => f._id === updatedForm._id
        );

        if (existingIndex >= 0) {
          this.$set(
            this.completedForms[indicationKey],
            existingIndex,
            updatedForm
          );
        }
      }
    },

    onEditCompletedForm(completedForm) {
      const indicationKey = Object.keys(this.completedForms).find((key) =>
        this.completedForms[key].some((f) => f._id === completedForm._id)
      );

      const originalForm = this.forms?.find(
        (f) => f._id === completedForm.form?._id
      );

      if (originalForm) {
        this.selectedFormForRenderer = { ...originalForm };
        this.editingFormData = {
          ...completedForm,
          indicationKey,
        };
        this.isEditingForm = true;
        this.isFormRendererVisible = true;
      }
    },

    onRemoveCompletedForm(completedForm, indicationKey) {
      if (this.completedForms[indicationKey]) {
        const index = this.completedForms[indicationKey].findIndex(
          (f) => f._id === completedForm._id
        );
        if (index >= 0) {
          this.completedForms[indicationKey].splice(index, 1);
        }
      }
    },

    getCompletedFormsForIndication(indicationKey) {
      return this.completedForms[indicationKey] || [];
    },

    async getForms() {
      try {
        const formService = await import("@/services/form");
        this.forms = await formService.default.getForms();
      } catch (error) {
        console.error("Error al cargar formularios:", error);
        this.forms = [];
      }
    },
  },
};
</script>

<template lang="pug">
.accordion-section(
  :class="{ 'accordion-section--active': isSelected, 'accordion-section--edit': mode === 'edit' }"
  :data-type="form.type"
)
  //- Header acordeón (modo crear)
  .accordion-header(v-if="mode !== 'edit'" @click="isSelected = !isSelected")
    .accordion-header__left
      .accordion-icon(:class="{ 'accordion-icon--active': isSelected }")
        i(:class="sectionIcon")
      h3.accordion-title {{ form.name }}
      .accordion-badge(v-if="savedIndicationsForType.length") {{ savedIndicationsForType.length }}
    .accordion-chevron(:class="{ 'accordion-chevron--open': isSelected }")
      i.el-icon-arrow-down

  //- Header acordeón (modo editar)
  .accordion-header.accordion-header--edit(v-else)
    .accordion-header__left
      .accordion-icon.accordion-icon--active
        i.el-icon-edit
      h3.accordion-title Editar {{ typeName || form.name }}

  //- Body con v-show para no destruir datos
  .accordion-body(v-show="mode === 'edit' || isSelected || hideHeader")
    span
      ValidationObserver(
        v-slot="{handleSubmit}"
        v-for="(indication, index) in actualIndications"
        :key="indication.key || indication._id"
      )
        form(@submit.prevent="handleSubmit(() => submit(indication))")
          //- Header de indicación múltiple
          .indication-multi-header(v-if="mode !== 'edit' && form.allowMultiple && indications.length > 1")
            .indication-multi-header__left
              .indication-number {{ index + 1 }}
              span {{ form.name }} {{ index + 1 }}
            button.btn-remove(type="button" @click="deleteIndication(indication.key)") Quitar

          //- Campos del formulario
          .form-fields
            .form-row(v-for="question in indication.defaultValues")
              .form-field
                ValidationProvider(:name="question.friendlyName" :rules="question.required ? 'required' : ''" v-slot="{ errors }")
                  span(v-if="question.inputType === 'title'")
                    label.field-label.field-label--title {{ question.friendlyName }}

                  span(v-else-if="question.inputType === 'consultationSelect'")
                    label.field-label {{ question.friendlyName }}
                    ConsultationSelect(
                      :isDisabled="!canEdit(indication)"
                      v-model="question.value"
                      :key="indication.key + '-' + question.friendlyName"
                    )
                  span(v-else-if="question.inputType === 'diagnosisSelect'")
                    label.field-label {{ question.friendlyName }}
                    DiagnosisSelect(
                      :isDisabled="!canEdit(indication)"
                      v-model="question.value"
                      :key="indication.key + '-' + question.friendlyName"
                      :ref="`sel-${indication.key}-diagnosisSelect`"
                    )

                  span(v-else-if="question.inputType === 'imagesSelect'")
                    ImagesSelect(
                      :isDisabled="!canEdit(indication)"
                      v-model="question.value"
                      :key="indication.key + '-' + question.friendlyName"
                      :ref="`sel-${indication.key}-imagesSelect`"
                    )

                  span(v-else-if="question.inputType === 'labSelect'")
                    LabSelect(
                      :isDisabled="!canEdit(indication)"
                      v-model="question.value"
                      :key="indication.key + '-' + question.friendlyName"
                      :ref="`sel-${indication.key}-labSelect`"
                    )

                  span(v-else-if="question.inputType === 'medicationSelect'")
                    label.field-label {{ question.friendlyName }}
                    MedicationSelect(
                      v-model="question.value"
                      :isDisabled="!canEdit(indication)"
                      :key="indication.key + '-' + question.friendlyName"
                      :ref="`sel-${indication.key}-medicationSelect`"
                    )

                  span(v-else-if="question.inputType === 'multiDate'")
                    label.field-label {{ question.friendlyName }}
                    MultiDatePicker(
                      v-model="question.value"
                      :isDisabled="!canEdit(indication)"
                      :key="indication.key + '-' + question.friendlyName"
                    )
                  span(v-else-if="question.inputType === 'formSelect'")
                    label.field-label {{ question.friendlyName }}
                    FormSelect(
                      v-model="question.value"
                      :isDisabled="!canEdit(indication)"
                      :completedForms="getCompletedFormsForIndication(indication.key)"
                      :key="indication.key + '-' + question.friendlyName"
                      @form-selected="onFormSelected"
                      @edit-completed-form="onEditCompletedForm"
                      @remove-completed-form="(form) => onRemoveCompletedForm(form, indication.key)"
                    )

                    FormRenderer(
                      v-if="isFormRendererVisible && selectedFormForRenderer"
                      :selectedForm="selectedFormForRenderer"
                      :patient="currentPatient"
                      :isVisible="isFormRendererVisible"
                      :prefillData="editingFormData"
                      :isEditing="isEditingForm"
                      @form-completed="(completedForm) => onFormRendererCompleted(completedForm, indication.key)"
                      @form-updated="onFormRendererUpdated"
                      @cancel-form="onFormRendererCancel"
                    )

                  span(v-else-if="question.inputType === 'programSelect'")
                    label.field-label {{ question.friendlyName }}
                    ProgramSelect(
                      v-model="question.value"
                      :isDisabled="!canEdit(indication)"
                      :key="indication.key + '-' + question.friendlyName"
                      @program-selected="onProgramSelected"
                    )

                    .program-info(v-if="selectedProgram")
                      .program-details
                        h4.program-name {{ selectedProgram.name }}
                        p.program-description(v-if="selectedProgram.description") {{ selectedProgram.description }}

                        .program-forms(v-if="programForms.length > 0")
                          el-collapse(v-model="activeCollapse" accordion)
                            el-collapse-item(
                              v-for="(form, index) in programForms"
                              :key="form._id"
                              :name="`form-${form._id}`"
                            )
                              template(slot="title")
                                .collapse-title-container
                                  span.collapse-title {{ form.name || form.title }}
                                  .completion-indicator
                                    .completion-circle(:class="{ 'completed': isFormCompletedForProgram(form._id, indication.key) }")
                                      i.el-icon-check(v-if="isFormCompletedForProgram(form._id, indication.key)")
                              FormRenderer(
                                :selectedForm="form"
                                :patient="currentPatient"
                                :isVisible="true"
                                :prefillData="null"
                                :isEditing="false"
                                @form-completed="(completedForm) => onProgramFormCompleted(completedForm, indication.key)"
                              )

                          el-collapse(v-model="activeCollapse" accordion)
                            el-collapse-item(name="prescription")
                              template(slot="title")
                                .collapse-title-container
                                  span.collapse-title Prescripción de Medicamentos
                                  .completion-indicator
                                    .completion-circle(:class="{ 'completed': prescriptionCompleted }")
                                      i.el-icon-check(v-if="prescriptionCompleted")

                              .form-content
                                fieldset.form-field(v-for="(field, idx) in prescriptionForm" :key="idx")
                                  label.field-label {{ field.friendlyName }}

                                  el-input(
                                    v-if="['input','textarea','number'].includes(field.inputType)"
                                    :type="field.inputType === 'textarea' ? 'textarea' : field.inputType"
                                    v-model="field.value"
                                    :placeholder="field.placeholder"
                                    :disabled="prescriptionDisabled"
                                  )

                                  DiagnosisSelect(
                                    v-else-if="field.inputType === 'diagnosisSelect'"
                                    v-model="field.value"
                                    :disabled="prescriptionDisabled"
                                  )

                                  MedicationSelect(
                                    v-else-if="field.inputType === 'medicationSelect'"
                                    v-model="field.value"
                                    :is-program="true"
                                    :programName="selectedProgram?.name"
                                    :isDisabled="prescriptionDisabled"
                                  )

                                  el-select(
                                    v-else-if="field.inputType === 'select'"
                                    v-model="field.value"
                                    placeholder="Seleccione una opción"
                                    :disabled="prescriptionDisabled"
                                  )
                                    el-option(
                                      v-for="opt in field.options"
                                      :key="opt.value"
                                      :label="opt.label"
                                      :value="opt.value"
                                    )

                                  MultiDatePicker(
                                    v-else-if="field.inputType === 'multiDate'"
                                    v-model="field.value"
                                    :disabled="prescriptionDisabled"
                                  )

                                  span.has-error(v-if="prescriptionTouched && field.required && !field.value")
                                    | Este campo es obligatorio

                                .form-actions
                                  button.btn-primary(
                                    type="button"
                                    :disabled="isPosting"
                                    @click="savePrescriptionIndication"
                                  ) Guardar Prescripción

                  span(v-else-if="question.inputType === 'checkbox'")
                    label.field-label {{ question.friendlyName }}
                    el-checkbox(v-model="question.value")

                  span(v-else-if="question.inputType === 'select'")
                    label.field-label {{ question.friendlyName }}
                    el-select(
                      v-model="question.value"
                      :disabled="!canEdit(indication)"
                      placeholder="Seleccione una opción"
                    )
                      el-option(
                        v-for="opt in question.options"
                        :key="opt.value"
                        :label="opt.label"
                        :value="opt.value"
                      )

                  span(v-else)
                    label.field-label {{ question.friendlyName }}
                    el-input(
                      :type="question.inputType"
                      :disabled="!canEdit(indication)"
                      v-model="question.value"
                      :placeholder="question.placeholder"
                      :key="indication.key + '-' + question.friendlyName"
                    )

                  span.has-error {{ errors[0] }}

          //- File upload
          .form-row
            .form-field
              file-upload(
                mode="list"
                v-show="indication.type !== 'program' && indication.type !== 'forms'"
                :url="uploadMediaEndpoint(indication._id)"
                :disabled="!canEdit(indication)"
                :ref="`fileUpload-${indication.key}`"
                :files="indication.files"
                :dropzone-options="getDropdownOptions()"
                @update:files="(newFiles) => indication.files = newFiles"
                @file-added="(file, files) => onFilesUpdated(files, indication)"
                @file-removed="(file, files) => onFilesUpdated(files, indication)"
                @files-removed="() => onFilesUpdated([], indication)"
                @fileupload-success="() => uploadFilesSuccess(indication.key)"
              )

          //- Botones de acción
          .form-actions
            button.btn-secondary(
              type="button"
              @click="mode === 'edit' ? cancelEdit() : revertIndication(indication, index)"
              :disabled="mode === 'edit' ? false : !canEdit(indication)"
            ) Cancelar
            button.btn-primary(
              type="submit"
              :disabled="mode === 'edit' ? isPosting : (indication._id || (selectedProgram && !areAllProgramFormsCompleted(indication.key, selectedProgram._id)))"
            ) {{ mode === 'edit' ? 'Guardar' : saveOrEditText(indication) }}

    //- Botón agregar más
    .add-indication(v-if="mode !== 'edit' && isSelected && form.allowMultiple")
      button.btn-add(type="button" @click="addIndication()") + Agregar {{ form.name ? form.name.toLowerCase() : 'nuevo' }}
</template>

<style lang="scss" scoped>
/* ============================================
   ACCORDION SECTION — Design System
   Navy: #002D5E | Teal: #2ABFBF | BG: #f8fafc
   ============================================ */

.accordion-section {
  background: white;
  border: 0.5px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
  transition: box-shadow 0.15s;

  &--active {
    border-color: #9fe1cb;
    box-shadow: 0 0 0 2px rgba(13, 157, 157, 0.08);

    .accordion-header {
      background: #f0fbfa;
    }

    .accordion-icon {
      background: #0d9d9d;

      i {
        color: #fff;
      }
    }
  }

  &--edit {
    border: 1.5px solid #0d9d9d;

    .accordion-header {
      background: #f0fafa;
    }
  }
}

.accordion-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 13px 16px;
  cursor: pointer;
  background: #fff;
  border-bottom: 0.5px solid #f1f5f9;
  transition: background 0.15s;
  user-select: none;

  &:hover {
    background: #f8fafc;
  }

  &--edit {
    cursor: default;
    &:hover {
      background: #f0fafa;
    }
  }

  &__left {
    display: flex;
    align-items: center;
    gap: 10px;
  }
}

.accordion-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.15s;

  i {
    font-size: 15px;
    color: #64748b;
    transition: color 0.15s;
  }

  &--active {
    background: #0d9d9d;
    i {
      color: #fff;
    }
  }
}

.accordion-title {
  font-size: 14px;
  font-weight: 700;
  color: #002d5e;
  margin: 0;
  letter-spacing: -0.1px;
}

.accordion-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 10px;
  background: #0d9d9d;
  color: white;
  font-size: 11px;
  font-weight: 700;
}

.accordion-chevron {
  color: #94a3b8;
  font-size: 13px;
  transition: transform 0.2s;

  &--open {
    transform: rotate(180deg);
  }
}

.accordion-body {
  padding: 0;
  border-top: 0.5px solid #f1f5f9;
}

.form-fields {
  padding: 14px 16px 8px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.form-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 0;
}

.form-field {
  margin-bottom: 4px;
}

.field-label {
  font-size: 10px;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 3px;
  display: block;

  &--title {
    font-size: 15px;
    font-weight: 600;
    text-transform: none;
    letter-spacing: 0;
    color: #002d5e;
    margin: 0 0 2px 0;
    display: block;
    line-height: 1.2;
  }
}

.has-error {
  font-size: 12px;
  color: #dc2626;
  margin-top: 4px;
}

.form-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: flex-end;
  padding: 12px 16px;
  border-top: 0.5px solid #f1f5f9;
  background: #fafbfd;
  margin-top: 4px;
}

.btn-primary {
  background: #002d5e;
  color: white;
  padding: 8px 20px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  font-family: "Plus Jakarta Sans", sans-serif;
  transition: background 0.15s;

  &:hover:not(:disabled) {
    background: #0a3d7a;
  }
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
}

.btn-secondary {
  background: white;
  color: #475569;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  font-family: "Plus Jakarta Sans", sans-serif;
  transition: background 0.15s;

  &:hover {
    background: #f8fafc;
  }
}

.btn-add {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #0d9d9d;
  background: none;
  border: none;
  cursor: pointer;
  padding: 10px 16px;

  &:hover {
    color: #085041;
  }
}

.btn-remove {
  font-size: 12px;
  color: #94a3b8;
  background: none;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 3px 10px;
  cursor: pointer;
  font-family: "Plus Jakarta Sans", sans-serif;
  font-weight: 600;
  transition: all 0.1s;

  &:hover {
    color: #dc2626;
    border-color: #fca5a5;
    background: #fff5f5;
  }
}

.add-indication {
  padding: 4px 16px 12px;
}

/* ---------- Multi-indication header ---------- */
.indication-multi-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f8fafc;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  margin-bottom: 16px;

  &__left {
    display: flex;
    align-items: center;
    gap: 10px;
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 14px;
    font-weight: 600;
    color: #002d5e;
  }
}

.indication-number {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  background: #002d5e;
  color: #fff;
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ---------- Program Info ---------- */
.program-info {
  margin-top: 15px;
  padding: 15px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e4e7ed;
}

.program-details {
  margin-bottom: 20px;
}

.program-name {
  margin: 0 0 8px 0;
  color: #303133;
  font-size: 16px;
  font-weight: 600;
}

.program-description {
  margin: 0;
  color: #606266;
  font-size: 14px;
  line-height: 1.5;
}

.program-forms {
  margin-top: 15px;
}

.form-content {
  padding: 15px;
}

.collapse-title-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 12px;
}

.collapse-title {
  flex: 1;
  font-weight: 500;
  color: #303133;
}

.completion-indicator {
  display: flex;
  align-items: center;
}

.completion-circle {
  width: 20px;
  height: 20px;
  border: 2px solid #dcdfe6;
  margin: 10px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #ffffff;
  transition: all 0.3s ease;
  flex-shrink: 0;

  &.completed {
    background-color: #67c23a;
    border-color: #67c23a;

    i {
      color: #ffffff;
      font-size: 12px;
      font-weight: bold;
    }
  }
}

// Evolución — motivo y diagnóstico en dos columnas
.accordion-section[data-type="evolution"] {
  .form-fields {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }

  // Descripción ocupa las dos columnas
  .form-row:last-child {
    grid-column: 1 / -1;
  }
}

// ── Evolución clínica — diseño compacto ──
.accordion-section[data-type="evolution"] {
  // Grid 2 columnas para motivo y diagnóstico
  .form-fields {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px 12px;
    padding: 12px 16px 6px;
  }

  // Descripción ocupa ancho completo
  .form-row:last-of-type {
    grid-column: 1 / -1;
  }

  // Labels compactos — igual que medicamentos
  .field-label {
    font-size: 10px !important;
    font-weight: 700 !important;
    color: #94a3b8 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.5px !important;
    margin-bottom: 2px !important;
    display: block;
  }

  .form-row {
    margin-bottom: 0;
  }

  // Inputs más bajos
  .el-input__inner {
    height: 34px !important;
    border-radius: 8px !important;
    border: 1.5px solid #e2e8f0 !important;
    background: #fff !important;
    font-size: 13px !important;
    font-family: "Plus Jakarta Sans", sans-serif !important;
    color: #1e293b !important;
    padding: 0 10px !important;

    &:focus {
      border-color: #0d9d9d !important;
      box-shadow: 0 0 0 3px rgba(13, 157, 157, 0.08) !important;
    }
  }

  // Textarea descripción — altura razonable
  .el-textarea__inner {
    border-radius: 8px !important;
    border: 1.5px solid #e2e8f0 !important;
    background: #fff !important;
    font-size: 13px !important;
    font-family: "Plus Jakarta Sans", sans-serif !important;
    color: #1e293b !important;
    min-height: 90px !important;
    padding: 8px 10px !important;
    resize: vertical !important;

    &:focus {
      border-color: #0d9d9d !important;
      box-shadow: 0 0 0 3px rgba(13, 157, 157, 0.08) !important;
    }
  }

  .el-select {
    width: 100% !important;
  }

  // File upload — su propia fila, chico y sin romper
  .form-row:has(.file-upload-wrapper),
  .form-row:has([class*="file-upload"]) {
    grid-column: 1 / -1;
    margin-top: 4px;

    .file-upload-wrapper,
    .dropzone {
      min-height: auto !important;
      padding: 6px 10px !important;
      border-radius: 8px !important;
      border: 1.5px dashed #e2e8f0 !important;
      background: #fafbfd !important;
    }

    .dz-message {
      margin: 0 !important;
      font-size: 11px !important;
      color: #94a3b8 !important;
    }
  }

  // Acciones — Guardar + Cancelar alineados a la izquierda
  .form-actions {
    padding: 8px 16px 10px;
    border-top: 0.5px solid #f1f5f9;
    background: #fafbfd;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 8px;

    &::before {
      display: none !important;
    }

    .btn-primary {
      background: #002d5e;
      color: white;
      padding: 7px 18px;
      border-radius: 8px;
      font-size: 13px;
      font-weight: 600;
      border: none;
      cursor: pointer;
      font-family: "Plus Jakarta Sans", sans-serif;

      &:hover:not(:disabled) {
        background: #0a3d7a;
      }
      &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
      }
    }

    .btn-secondary {
      background: white;
      color: #475569;
      padding: 7px 14px;
      border-radius: 8px;
      font-size: 13px;
      font-weight: 600;
      border: 1px solid #e2e8f0;
      cursor: pointer;
      font-family: "Plus Jakarta Sans", sans-serif;

      &:hover {
        background: #f8fafc;
      }
    }
  }
}

// ── Prescripciones — diseño v5 ──
.accordion-section[data-type="medications"] {
  .accordion-header {
    border-bottom: 1px solid #f1f5f9;
  }

  .indication-multi-header {
    background: transparent;
    border: none;
    border-bottom: 0.5px solid #f1f5f9;
    border-radius: 0;
    padding: 8px 16px;
    margin-bottom: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;

    .indication-multi-header__left {
      display: flex;
      align-items: center;
      gap: 10px;
      font-size: 14px;
      font-weight: 500;
      color: #1e293b;

      .indication-number {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: #0d9d9d;
        font-size: 0;
        flex-shrink: 0;
      }
    }

    .btn-remove {
      font-size: 12px;
      color: #94a3b8;
      background: none;
      border: 1px solid #e2e8f0;
      border-radius: 6px;
      padding: 3px 10px;
      cursor: pointer;
      font-family: "Plus Jakarta Sans", sans-serif;
      font-weight: 600;
      transition: all 0.1s;

      &:hover {
        color: #dc2626;
        border-color: #fca5a5;
        background: #fff5f5;
      }
    }
  }

  .form-fields {
    display: grid;
    grid-template-columns: 2fr 1fr;
    grid-template-rows: auto auto auto auto auto auto auto;
    gap: 8px 12px;
    padding: 12px 16px 6px;
  }

  // 1. Medicamento — col 1, fila 1
  .form-row:nth-child(1) {
    grid-column: 1 / 2;
    grid-row: 1;
  }

  // 2. Cantidad — col 2, fila 1
  .form-row:nth-child(2) {
    grid-column: 2 / 3;
    grid-row: 1;
  }

  // 3. Genérico — col 1, fila 2
  .form-row:nth-child(3) {
    grid-column: 1 / 2;
    grid-row: 2;
  }

  // 4. Posología — col 1+2, fila 3 (textarea chica)
  .form-row:nth-child(4) {
    grid-column: 1 / -1;
    grid-row: 3;
  }

  // 5. Tratamiento prolongado — col 2, fila 2 (mismo row que Genérico)
  .form-row:nth-child(5) {
    grid-column: 2 / 3;
    grid-row: 2;
  }

  // 6. Diagnóstico — ancho completo, fila 4
  .form-row:nth-child(6) {
    grid-column: 1 / -1;
    grid-row: 4;
  }

  // 7. Observaciones — ancho completo, fila 5
  .form-row:nth-child(7) {
    grid-column: 1 / -1;
    grid-row: 5;
  }

  // 8. Email — ancho completo, fila 6
  .form-row:nth-child(8) {
    grid-column: 1 / -1;
    grid-row: 6;
  }

  // File upload y resto — ancho completo
  .form-row:nth-child(n + 9) {
    grid-column: 1 / -1;
  }

  // Labels compactos
  .field-label {
    font-size: 10px !important;
    font-weight: 700 !important;
    color: #94a3b8 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.5px !important;
    margin-bottom: 2px !important;
    display: block;
  }

  .form-row {
    margin-bottom: 0;
  }

  // Inputs bajos
  .el-input__inner {
    height: 34px !important;
    border-radius: 8px !important;
    border: 1.5px solid #e2e8f0 !important;
    background: #fff !important;
    font-size: 13px !important;
    font-family: "Plus Jakarta Sans", sans-serif !important;
    color: #1e293b !important;
    padding: 0 10px !important;

    &:focus {
      border-color: #0d9d9d !important;
      box-shadow: 0 0 0 3px rgba(13, 157, 157, 0.08) !important;
    }
  }

  // Textareas — máximo 2 filas de alto
  .el-textarea__inner {
    border-radius: 8px !important;
    border: 1.5px solid #e2e8f0 !important;
    background: #fff !important;
    font-size: 13px !important;
    font-family: "Plus Jakarta Sans", sans-serif !important;
    color: #1e293b !important;
    min-height: 36px !important;
    max-height: 48px !important;
    padding: 8px 10px !important;
    resize: none !important;

    &:focus {
      border-color: #0d9d9d !important;
      box-shadow: 0 0 0 3px rgba(13, 157, 157, 0.08) !important;
    }
  }

  .el-select {
    width: 100% !important;
  }

  // Acciones
  .form-actions {
    padding: 8px 16px 10px;
    border-top: 0.5px solid #f1f5f9;
    background: #fafbfd;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 8px;

    .btn-primary {
      order: -1;
      background: #002d5e;
      color: white;
      padding: 7px 18px;
      border-radius: 8px;
      font-size: 13px;
      font-weight: 600;
      border: none;
      cursor: pointer;
      font-family: "Plus Jakarta Sans", sans-serif;

      &:hover:not(:disabled) {
        background: #0a3d7a;
      }
      &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
      }
    }

    .btn-secondary {
      background: white;
      color: #475569;
      padding: 7px 14px;
      border-radius: 8px;
      font-size: 13px;
      font-weight: 600;
      border: 1px solid #e2e8f0;
      cursor: pointer;
      font-family: "Plus Jakarta Sans", sans-serif;

      &:hover {
        background: #f8fafc;
      }
    }
  }
}
</style>

<!-- Non-scoped styles for Element UI input overrides -->
<style lang="scss">
.accordion-section {
  .el-input__inner,
  .el-textarea__inner {
    padding: 10px 14px;
    border: 1.5px solid #e2e8f0;
    border-radius: 10px;
    background: #f8fafc;
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 14px;
    transition: all 0.2s;

    &:focus {
      background: #fff;
      border-color: #002d5e;
      box-shadow: 0 0 0 3px rgba(0, 45, 94, 0.08);
    }
  }

  .el-textarea__inner {
    resize: vertical;
  }

  .el-select {
    width: 100%;
  }

  .el-switch {
    height: 22px;

    .el-switch__core {
      height: 22px;
      width: 40px !important;
      border-radius: 11px;

      &::after {
        width: 18px;
        height: 18px;
      }
    }
  }

  .program-forms {
    .el-collapse {
      border: none;

      .el-collapse-item {
        margin-bottom: 12px;
        border-radius: 8px;
        overflow: hidden;

        .el-collapse-item__header {
          background-color: #f8f9fa;
          border-bottom: 1px solid #e4e7ed;
          border-radius: 8px 8px 0 0;
          padding: 12px 15px;

          &:hover {
            background-color: #f8f9fa;
          }

          &.is-active {
            background-color: #f8f9fa;
            border-bottom: 1px solid #e4e7ed;
            border-radius: 8px 8px 0 0;
          }
        }

        .el-collapse-item__wrap {
          border: none;
          background-color: #ffffff;

          .el-collapse-item__content {
            padding: 0;
            border-radius: 0 0 8px 8px;
          }
        }
      }
    }
  }
}

/* ── Accordion headers — estilo unificado ── */
.accordion-section .accordion-header {
  padding: 13px 16px;
  border-bottom: 0.5px solid #f1f5f9;
  background: #fff;
}

.accordion-section .accordion-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #eef2f7;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  i {
    font-size: 15px;
    color: #475569;
  }
}

.accordion-section--active .accordion-icon {
  background: #0d9d9d;
  i {
    color: #fff;
  }
}

.accordion-section .accordion-title {
  font-size: 14px;
  font-weight: 700;
  color: #002d5e;
  margin: 0;
}

.accordion-section .accordion-chevron {
  color: #94a3b8;
  font-size: 13px;
  transition: transform 0.2s;
}

.accordion-section .accordion-chevron--open {
  transform: rotate(180deg);
}

.accordion-section[data-type="medications"] .el-textarea__inner {
  min-height: 36px !important;
  max-height: 48px !important;
  height: 48px !important;
}
</style>
